import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import {
  Crown, Users, CreditCard, ArrowRightLeft, Plus, CheckCircle, XCircle,
  UserMinus, Loader2, Coins,
} from 'lucide-react'

interface Props {
  associationId:   string
  associationName: string
  memberId:        string
  positionTitle:   string
  tenureStart:     string
  permissions:     Record<string, boolean>
}

const COMMON_CURRENCIES = [
  { code: 'USD', name: 'US Dollar' }, { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British Pound' }, { code: 'XOF', name: 'West African CFA Franc' },
  { code: 'XAF', name: 'Central African CFA Franc' }, { code: 'NGN', name: 'Nigerian Naira' },
  { code: 'GHS', name: 'Ghanaian Cedi' }, { code: 'KES', name: 'Kenyan Shilling' },
  { code: 'ZAR', name: 'South African Rand' }, { code: 'EGP', name: 'Egyptian Pound' },
  { code: 'ETB', name: 'Ethiopian Birr' }, { code: 'TZS', name: 'Tanzanian Shilling' },
  { code: 'UGX', name: 'Ugandan Shilling' }, { code: 'RWF', name: 'Rwandan Franc' },
  { code: 'GMD', name: 'Gambian Dalasi' }, { code: 'SLE', name: 'Sierra Leonean Leone' },
  { code: 'LRD', name: 'Liberian Dollar' }, { code: 'MZN', name: 'Mozambican Metical' },
  { code: 'ZMW', name: 'Zambian Kwacha' }, { code: 'BWP', name: 'Botswana Pula' },
]

export function AssociationOfficerPanel({
  associationId,
  associationName,
  memberId,
  positionTitle,
  tenureStart,
  permissions,
}: Props) {
  const { toast } = useToast()
  const qc = useQueryClient()

  // local state
  const [assignModal,      setAssignModal]      = useState<{ positionId: string; title: string } | null>(null)
  const [assignMemberId,   setAssignMemberId]   = useState('')
  const [transferModal,    setTransferModal]    = useState(false)
  const [transferMemberId, setTransferMemberId] = useState('')
  const [currencyModal,    setCurrencyModal]    = useState(false)
  const [currencyForm,     setCurrencyForm]     = useState({ currency_code: '', currency_name: '', exchange_rate_to_primary: '1' })
  const [duesEdit,         setDuesEdit]         = useState(false)
  const [duesAmount,       setDuesAmount]       = useState('')

  // ── queries ──────────────────────────────────────────────────────────────

  const { data: assocData } = useQuery({
    queryKey: ['portal-assoc-detail', associationId],
    queryFn: async () => {
      const { data } = await (supabase
        .from('associations' as any)
        .select('dues_amount, dues_currency, dues_period')
        .eq('id', associationId)
        .single() as any)
      return data as any
    },
  })

  const { data: positions } = useQuery({
    queryKey: ['portal-assoc-positions', associationId],
    enabled: !!permissions.can_assign_leadership,
    queryFn: async () => {
      const { data } = await (supabase
        .from('association_positions' as any)
        .select('*')
        .eq('association_id', associationId)
        .eq('is_active', true)
        .order('rank') as any)
      return (data || []) as any[]
    },
  })

  const { data: leadership, refetch: refetchLeadership } = useQuery({
    queryKey: ['portal-assoc-leadership', associationId],
    enabled: !!permissions.can_assign_leadership,
    queryFn: async () => {
      const { data } = await (supabase
        .from('association_leadership' as any)
        .select(`*, association_positions(title, rank), association_members!inner(journalist_profiles(full_name))`)
        .eq('association_id', associationId)
        .eq('is_current', true) as any)
      return (data || []) as any[]
    },
  })

  const { data: activeMembers } = useQuery({
    queryKey: ['portal-assoc-active-members', associationId],
    enabled: !!(permissions.can_assign_leadership || permissions.can_add_members),
    queryFn: async () => {
      const { data } = await (supabase
        .from('association_members' as any)
        .select('id, journalist_id, journalist_profiles(full_name, email)')
        .eq('association_id', associationId)
        .eq('status', 'active') as any)
      return (data || []) as any[]
    },
  })

  const { data: pendingMembers, refetch: refetchPending } = useQuery({
    queryKey: ['portal-assoc-pending', associationId],
    enabled: !!permissions.can_add_members,
    queryFn: async () => {
      const { data } = await (supabase
        .from('association_members' as any)
        .select('id, journalist_id, journalist_profiles(full_name, email), news_org, country_code, applied_at')
        .eq('association_id', associationId)
        .eq('status', 'pending')
        .order('applied_at', { ascending: false }) as any)
      return (data || []) as any[]
    },
  })

  const { data: currencies, refetch: refetchCurrencies } = useQuery({
    queryKey: ['portal-assoc-currencies', associationId],
    enabled: !!permissions.can_edit_dues,
    queryFn: async () => {
      const { data } = await (supabase
        .from('association_currencies' as any)
        .select('*')
        .eq('association_id', associationId)
        .order('is_primary', { ascending: false }) as any)
      return (data || []) as any[]
    },
  })

  const filledMap = Object.fromEntries(
    (leadership || []).map((l: any) => [l.position_id, l])
  )

  // ── mutations ─────────────────────────────────────────────────────────────

  const assignPositionMutation = useMutation({
    mutationFn: async ({ positionId, memId }: { positionId: string; memId: string }) => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')
      const member = (activeMembers || []).find((m: any) => m.id === memId) as any
      const { error } = await (supabase.from('association_leadership' as any).insert({
        association_id: associationId,
        position_id:    positionId,
        member_id:      memId,
        user_id:        member.journalist_id,
        assigned_by:    user.id,
        assignment_type: 'president_assign',
      }) as any)
      if (error) throw error
    },
    onSuccess: () => {
      toast({ title: 'Position assigned.' })
      setAssignModal(null); setAssignMemberId('')
      refetchLeadership()
    },
    onError: (e: any) => toast({ title: 'Failed.', description: e.message, variant: 'destructive' }),
  })

  const vacatePositionMutation = useMutation({
    mutationFn: async (leadershipId: string) => {
      const { error } = await (supabase.from('association_leadership' as any)
        .update({ tenure_end: new Date().toISOString().slice(0, 10), is_current: false, vacated_reason: 'removed' })
        .eq('id', leadershipId) as any)
      if (error) throw error
    },
    onSuccess: () => { toast({ title: 'Position vacated.' }); refetchLeadership() },
  })

  const transferMutation = useMutation({
    mutationFn: async (newMemberId: string) => {
      const { data: { user } } = await supabase.auth.getUser()
      const { error } = await (supabase.rpc('transfer_association_presidency' as any, {
        p_association_id:  associationId,
        p_new_member_id:   newMemberId,
        p_assigned_by:     user?.id,
        p_assignment_type: 'president_transfer',
      }) as any)
      if (error) throw error
    },
    onSuccess: () => {
      toast({ title: 'Presidency transferred. All positions vacated, assign your team.' })
      setTransferModal(false); setTransferMemberId('')
      refetchLeadership()
      qc.invalidateQueries({ queryKey: ['portal-my-leadership'] })
    },
    onError: (e: any) => toast({ title: 'Transfer failed.', description: e.message, variant: 'destructive' }),
  })

  const approveMutation = useMutation({
    mutationFn: async ({ id, approve }: { id: string; approve: boolean }) => {
      const { data: { user } } = await supabase.auth.getUser()
      const { error } = await (supabase.from('association_members' as any).update({
        status: approve ? 'active' : 'rejected',
        approved_at: approve ? new Date().toISOString() : null,
        approved_by: approve ? user?.id : null,
        updated_at: new Date().toISOString(),
      }).eq('id', id) as any)
      if (error) throw error
    },
    onSuccess: () => { toast({ title: 'Application updated.' }); refetchPending() },
  })

  const saveDuesMutation = useMutation({
    mutationFn: async () => {
      const { error } = await (supabase.from('associations' as any)
        .update({ dues_amount: parseFloat(duesAmount) })
        .eq('id', associationId) as any)
      if (error) throw error
    },
    onSuccess: () => {
      toast({ title: 'Dues updated.' })
      setDuesEdit(false)
      qc.invalidateQueries({ queryKey: ['portal-assoc-detail', associationId] })
    },
    onError: (e: any) => toast({ title: 'Failed.', description: e.message, variant: 'destructive' }),
  })

  const addCurrencyMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      const { error } = await (supabase.from('association_currencies' as any).upsert({
        association_id: associationId,
        currency_code:  currencyForm.currency_code.toUpperCase(),
        currency_name:  currencyForm.currency_name,
        exchange_rate_to_primary: parseFloat(currencyForm.exchange_rate_to_primary) || 1,
        is_primary: false, is_active: true,
        rate_updated_at: new Date().toISOString(),
        updated_by: user?.id,
      }, { onConflict: 'association_id,currency_code' }) as any)
      if (error) throw error
    },
    onSuccess: () => {
      toast({ title: 'Currency added.' })
      setCurrencyModal(false)
      setCurrencyForm({ currency_code: '', currency_name: '', exchange_rate_to_primary: '1' })
      refetchCurrencies()
    },
    onError: (e: any) => toast({ title: 'Failed.', description: e.message, variant: 'destructive' }),
  })

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="border-2 border-accent/40 rounded-xl overflow-hidden">
      {/* position banner */}
      <div className="bg-primary px-5 py-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Crown size={18} className="text-accent flex-shrink-0" />
          <div>
            <div className="text-white font-semibold text-[14px]">
              {positionTitle}, {associationName}
            </div>
            <div className="text-accent-foreground/70 text-[11px] mt-0.5">
              In office since {new Date(tenureStart).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>
        </div>
        {permissions.can_transfer_presidency && (
          <button
            onClick={() => setTransferModal(true)}
            className="flex items-center gap-1.5 text-[11.5px] px-3 py-1.5 border border-accent/50 text-accent-foreground rounded-lg hover:bg-white/10 transition-colors flex-shrink-0"
          >
            <ArrowRightLeft size={12} /> Transfer Presidency
          </button>
        )}
      </div>

      <div className="p-5 space-y-6 bg-background">

        {/* ── Leadership Management ── */}
        {permissions.can_assign_leadership && (
          <section className="space-y-3">
            <h4 className="text-[13px] font-semibold text-foreground flex items-center gap-1.5">
              <Crown size={13} className="text-accent" /> Leadership Positions
            </h4>

            <div className="rounded-xl border overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="bg-muted">
                    <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Position</th>
                    <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Holder</th>
                    <th className="text-right px-4 py-2 font-semibold text-muted-foreground">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(positions || []).map((pos: any, idx: number) => {
                    const filled = filledMap[pos.id]
                    return (
                      <tr key={pos.id} className={`border-t border-border ${idx % 2 === 1 ? 'bg-muted/50' : ''}`}>
                        <td className="px-4 py-2.5 font-medium">
                          {pos.title}
                          {pos.title === 'President' && <Crown size={10} className="inline ml-1 text-accent" />}
                        </td>
                        <td className="px-4 py-2.5">
                          {filled
                            ? <span>{filled.association_members?.journalist_profiles?.full_name ?? 'Unknown'}</span>
                            : <span className="text-muted-foreground italic">Vacant</span>}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {filled ? (
                            pos.title !== 'President' && (
                              <button
                                onClick={() => vacatePositionMutation.mutate(filled.id)}
                                disabled={vacatePositionMutation.isPending}
                                className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-muted text-muted-foreground hover:bg-destructive/12 hover:text-destructive transition-colors ml-auto"
                              >
                                <UserMinus size={11} /> Vacate
                              </button>
                            )
                          ) : (
                            pos.title !== 'President' && (
                              <button
                                onClick={() => { setAssignModal({ positionId: pos.id, title: pos.title }); setAssignMemberId('') }}
                                className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-primary/8 text-primary font-medium hover:bg-primary/90 hover:text-white transition-colors ml-auto"
                              >
                                <Plus size={11} /> Assign
                              </button>
                            )
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* ── Member Management ── */}
        {permissions.can_add_members && (
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h4 className="text-[13px] font-semibold text-foreground flex items-center gap-1.5">
                <Users size={13} /> Pending Applications
                {(pendingMembers || []).length > 0 && (
                  <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[hsl(var(--warning)/0.18)] text-warning font-bold">
                    {(pendingMembers || []).length}
                  </span>
                )}
              </h4>
            </div>

            {(pendingMembers || []).length === 0 ? (
              <p className="text-justify text-[12px] text-muted-foreground">No pending applications.</p>
            ) : (
              <div className="space-y-2">
                {(pendingMembers || []).map((m: any) => (
                  <div key={m.id} className="flex items-center justify-between gap-3 border border-border rounded-xl p-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium">{m.journalist_profiles?.full_name ?? 'Unknown'}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {m.journalist_profiles?.email}
                        {m.news_org ? ` · ${m.news_org}` : ''}
                        {m.country_code ? ` · ${m.country_code}` : ''}
                      </div>
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => approveMutation.mutate({ id: m.id, approve: true })}
                        className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md bg-success/15 text-success font-medium hover:bg-success hover:text-success-foreground transition-colors"
                      >
                        <CheckCircle size={11} /> Approve
                      </button>
                      <button
                        onClick={() => approveMutation.mutate({ id: m.id, approve: false })}
                        className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md bg-destructive/12 text-destructive font-medium hover:bg-destructive hover:text-destructive-foreground transition-colors"
                      >
                        <XCircle size={11} /> Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ── Dues & Currencies ── */}
        {permissions.can_edit_dues && (
          <section className="space-y-3">
            <h4 className="text-[13px] font-semibold text-foreground flex items-center gap-1.5">
              <Coins size={13} /> Dues & Currencies
            </h4>

            {/* current dues */}
            <div className="bg-muted rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between text-[12.5px]">
                <span className="text-muted-foreground">Current Dues</span>
                {duesEdit ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={duesAmount}
                      onChange={e => setDuesAmount(e.target.value)}
                      className="w-24 border border-border rounded px-2 py-1 text-[12px] bg-background"
                      placeholder={assocData?.dues_amount}
                    />
                    <span className="text-muted-foreground">{assocData?.dues_currency} / {assocData?.dues_period}</span>
                    <button
                      onClick={() => saveDuesMutation.mutate()}
                      disabled={saveDuesMutation.isPending}
                      className="text-[11px] px-2 py-1 bg-success text-success-foreground rounded hover:bg-success disabled:opacity-50 transition-colors"
                    >
                      Save
                    </button>
                    <button onClick={() => setDuesEdit(false)} className="text-[11px] px-2 py-1 border border-border rounded hover:bg-muted transition-colors">Cancel</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">
                      {assocData?.dues_currency} {parseFloat(assocData?.dues_amount ?? 0).toFixed(2)} / {assocData?.dues_period}
                    </span>
                    <button onClick={() => { setDuesAmount(assocData?.dues_amount?.toString() ?? ''); setDuesEdit(true) }}
                      className="text-[11px] text-muted-foreground hover:text-foreground underline transition-colors">Edit</button>
                  </div>
                )}
              </div>
            </div>

            {/* currencies */}
            <div className="flex items-center justify-between">
              <p className="text-justify text-[11.5px] text-muted-foreground">Exchange rates for member states</p>
              <button
                onClick={() => setCurrencyModal(true)}
                className="flex items-center gap-1.5 text-[11.5px] px-2.5 py-1.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/80 transition-colors"
              >
                <Plus size={11} /> Add Currency
              </button>
            </div>

            {(currencies || []).length > 0 && (
              <div className="rounded-xl border overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="bg-muted">
                      <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Currency</th>
                      <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Rate</th>
                      <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Dues In Currency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(currencies || []).map((c: any) => {
                      const eq = parseFloat(assocData?.dues_amount ?? 0) * parseFloat(c.exchange_rate_to_primary ?? 1)
                      return (
                        <tr key={c.id} className="border-t border-border">
                          <td className="px-4 py-2.5">
                            <span className="font-semibold">{c.currency_code}</span>
                            {c.is_primary && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-accent/20 text-warning font-semibold">PRIMARY</span>}
                          </td>
                          <td className="px-4 py-2.5 text-muted-foreground">{c.is_primary ? '1 (base)' : `×${parseFloat(c.exchange_rate_to_primary).toFixed(4)}`}</td>
                          <td className="px-4 py-2.5 font-medium">{c.currency_code} {eq.toLocaleString('en', { maximumFractionDigits: 2 })}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </div>

      {/* ─── Assign Position modal ─── */}
      {assignModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-[15px] font-semibold mb-1">Assign {assignModal.title}</h3>
            <p className="text-justify text-[12px] text-muted-foreground mb-4">Select an active member to hold this position.</p>
            <select value={assignMemberId} onChange={e => setAssignMemberId(e.target.value)}
              className="w-full border border-border rounded-lg px-3 py-2 text-[13px] bg-background mb-4">
              <option value="">Select member…</option>
              {(activeMembers || []).map((m: any) => (
                <option key={m.id} value={m.id}>{m.journalist_profiles?.full_name ?? m.id} {m.journalist_profiles?.email ? `(${m.journalist_profiles.email})` : ''}</option>
              ))}
            </select>
            <div className="flex gap-3">
              <button onClick={() => { setAssignModal(null); setAssignMemberId('') }}
                className="flex-1 py-2.5 border border-border rounded-lg text-[13px] hover:bg-muted transition-colors">Cancel</button>
              <button
                disabled={!assignMemberId || assignPositionMutation.isPending}
                onClick={() => assignPositionMutation.mutate({ positionId: assignModal.positionId, memId: assignMemberId })}
                className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-lg text-[13px] font-medium hover:bg-primary/80 disabled:opacity-50 transition-colors">
                {assignPositionMutation.isPending ? 'Assigning…' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Transfer Presidency modal ─── */}
      {transferModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-2 mb-1">
              <ArrowRightLeft size={16} className="text-accent" />
              <h3 className="text-[15px] font-semibold">Transfer Presidency</h3>
            </div>
            <div className="bg-[hsl(var(--warning)/0.08)] border border-[hsl(var(--warning)/0.3)] rounded-lg p-3 mb-4 text-[11.5px] text-warning">
              <strong>Important:</strong> Transferring the presidency will <strong>vacate all current exco positions</strong>.
              The incoming president must assemble their own team.
            </div>
            <select value={transferMemberId} onChange={e => setTransferMemberId(e.target.value)}
              className="w-full border border-border rounded-lg px-3 py-2 text-[13px] bg-background mb-4">
              <option value="">Select new president…</option>
              {(activeMembers || []).filter((m: any) => m.id !== memberId).map((m: any) => (
                <option key={m.id} value={m.id}>{m.journalist_profiles?.full_name ?? m.id} {m.journalist_profiles?.email ? `(${m.journalist_profiles.email})` : ''}</option>
              ))}
            </select>
            <div className="flex gap-3">
              <button onClick={() => { setTransferModal(false); setTransferMemberId('') }}
                className="flex-1 py-2.5 border border-border rounded-lg text-[13px] hover:bg-muted transition-colors">Cancel</button>
              <button
                disabled={!transferMemberId || transferMutation.isPending}
                onClick={() => transferMutation.mutate(transferMemberId)}
                className="flex-1 py-2.5 bg-accent text-foreground rounded-lg text-[13px] font-semibold hover:bg-accent disabled:opacity-50 transition-colors">
                {transferMutation.isPending ? 'Transferring…' : 'Confirm Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Add Currency modal ─── */}
      {currencyModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-[15px] font-semibold mb-4">Add Currency</h3>
            <div className="space-y-3">
              <div>
                <label className="text-[12px] font-medium text-muted-foreground block mb-1">Currency</label>
                <select value={currencyForm.currency_code}
                  onChange={e => {
                    const c = COMMON_CURRENCIES.find(x => x.code === e.target.value)
                    setCurrencyForm(f => ({ ...f, currency_code: e.target.value, currency_name: c?.name ?? f.currency_name }))
                  }}
                  className="w-full border border-border rounded-lg px-3 py-2 text-[13px] bg-background">
                  <option value="">Select currency…</option>
                  {COMMON_CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code}, {c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[12px] font-medium text-muted-foreground block mb-1">Exchange Rate (vs primary)</label>
                <input type="number" value={currencyForm.exchange_rate_to_primary}
                  onChange={e => setCurrencyForm(f => ({ ...f, exchange_rate_to_primary: e.target.value }))}
                  placeholder="e.g. 1550 for NGN per USD"
                  className="w-full border border-border rounded-lg px-3 py-2 text-[13px] bg-background focus:outline-none" />
                {currencyForm.currency_code && parseFloat(currencyForm.exchange_rate_to_primary) > 0 && (
                  <p className="text-justify text-[11px] text-muted-foreground mt-1">
                    Dues preview: {currencyForm.currency_code} {(parseFloat(assocData?.dues_amount ?? 0) * parseFloat(currencyForm.exchange_rate_to_primary)).toLocaleString('en', { maximumFractionDigits: 2 })} / {assocData?.dues_period}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => { setCurrencyModal(false); setCurrencyForm({ currency_code: '', currency_name: '', exchange_rate_to_primary: '1' }) }}
                className="flex-1 py-2.5 border border-border rounded-lg text-[13px] hover:bg-muted transition-colors">Cancel</button>
              <button
                disabled={!currencyForm.currency_code || addCurrencyMutation.isPending}
                onClick={() => addCurrencyMutation.mutate()}
                className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-lg text-[13px] font-medium hover:bg-primary/80 disabled:opacity-50 transition-colors">
                {addCurrencyMutation.isPending ? 'Saving…' : 'Add Currency'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
